/**
 * FounderSpotsTicker — live scarcity badge for the landing hero.
 *
 * Reads `GET /api/founder-access/spots` (public, no auth) and surfaces the
 * count above-the-fold so visitors who never trigger the exit-intent popup
 * still see the urgency. Clicking it programmatically opens the
 * FounderAccessPopup via a custom event ("founder-access:open").
 *
 * Hides itself when:
 *   - The API call fails (non-blocking — hero stays clean).
 *   - All 10 spots are taken (we don't want to advertise a sold-out program;
 *     in that case we just don't render).
 *   - `remaining` >= `max_spots` (cohort not started yet — don't tease).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Flame } from "lucide-react";

const API_BASE =
  (typeof process !== "undefined" && process.env?.REACT_APP_BACKEND_URL) || "";

export default function FounderSpotsTicker({ className = "" }) {
  const [spots, setSpots] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/api/founder-access/spots`)
      .then((r) => { if (!cancelled) setSpots(r.data); })
      .catch(() => { /* silently skip */ });
    return () => { cancelled = true; };
  }, []);

  if (!spots) return null;
  const remaining = Number(spots.remaining ?? 0);
  const max = Number(spots.max_spots ?? 10);
  const filled = Math.max(0, Math.min(max, max - remaining));

  // Don't render in either edge: sold out, or "all spots still open"
  // (looks weak — defeats the scarcity message).
  if (remaining <= 0 || remaining >= max) return null;

  const handleClick = () => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("founder-access:open"));
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-100 via-amber-50 to-emerald-50 border border-amber-300 text-[12px] font-bold text-amber-900 hover:from-amber-200 hover:via-amber-100 hover:to-emerald-100 hover:border-amber-400 transition-all shadow-sm hover:shadow group ${className}`}
      data-testid="founder-spots-ticker"
      aria-label={`Apply for Priority Access — ${filled} of ${max} spots taken`}
    >
      <span className="relative inline-flex items-center">
        <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
        <span className="absolute -inset-1 rounded-full bg-amber-400 opacity-40 animate-ping" />
      </span>
      <Flame size={12} className="text-amber-600" />
      <span className="hidden sm:inline">Priority Access</span>
      <span className="hidden sm:inline">·</span>
      <span data-testid="founder-spots-ticker-count">
        <strong className="text-amber-950">{filled}</strong>/{max}
      </span>
      <span className="text-amber-800 font-semibold">spots taken</span>
      <span className="ml-1 text-emerald-700 group-hover:translate-x-0.5 transition-transform">→</span>
    </button>
  );
}
